import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import type { HotelEdit_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { ToastAction } from "@/components/ui/toast/toast";

import * as Shared from "../../../../../_shared";
import { useHotelArticleUpdateMutation } from "../../repository/mutation/use-hotel-article-update-mutation";

type Props = {
	hotelID: string;
	data: HotelEdit_DesignPageQuery;
};

export const useInitialized = ({ hotelID, data }: Props) => {
	const { onUpdate: _onUpdate } = useHotelArticleUpdateMutation({ hotelID });
	const { article, tags, tagsLocation } = data.hotel;

	const navigate = useNavigate();
	const hotelListPath = generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, { hotelID });

	const onNavigateToHotelList = useCallback(() => {
		navigate(hotelListPath);
	}, [hotelListPath, navigate]);

	const onUpdateSuccessCallback = useCallback(() => {
		notifyUserEventInfo({
			title: "施設記事を保存しました",
			action: (
				<ToastAction onClick={onNavigateToHotelList} altText="一覧に戻る">
					一覧に戻る
				</ToastAction>
			),
		});
	}, [onNavigateToHotelList]);

	const { articleHTML, onSetArticleHTML, tag, onSetTag, isDirty, isValid, onUpdate } =
		Shared.Model.UseCase.useCreateMagazineEditorForDefaultModel({
			article,
			tagList: tags,
			/**
			 * tagsLocation の index は以下の定義順となっている
			 */
			regionTag: tagsLocation[0],
			prefectureTag: tagsLocation[1],
			cityTag: tagsLocation[2],
			onUpdate: _onUpdate,
			onUpdateSuccessCallback,
		});

	return {
		data,
		articleHTML,
		onSetArticleHTML,
		tag,
		onSetTag,
		isDirty,
		isValid,
		onUpdate,
	};
};
